/**
 * `/templates` — public gallery of plans published after ≥5 of their tasks passed verification
 * (moat plan §7). Landing-styled (`.mk`, light-only); data from `GET /api/public/templates`.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Container, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { usePublicTemplates, type TemplateCard } from '#/lib/api-flywheel'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/templates/')({
  validateSearch: (s: Record<string, unknown>): { tag?: string } => ({ tag: typeof s.tag === 'string' && s.tag ? s.tag : undefined }),
  head: () => ({
    meta: [
      { title: m.fw_tpl_meta_title() },
      { name: 'description', content: m.fw_tpl_meta_desc() },
      { property: 'og:title', content: m.fw_tpl_meta_title() },
      { property: 'og:description', content: m.fw_tpl_meta_desc() },
    ],
  }),
  component: Page,
})


function Card({ t }: { t: TemplateCard }) {
  return (
    <Link
      to="/templates/$slug"
      params={{ slug: t.slug }}
      className="mk-card-white flex flex-col gap-3 p-6 transition-colors hover:border-[var(--mk-fg)]"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0">{t.title}</h3>
        <span className="shrink-0 rounded border border-[var(--mk-border)] px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[var(--mk-fg-muted)]">{t.locale}</span>
      </div>
      <p className="mk-muted line-clamp-3 text-[15px] leading-relaxed">{t.summary}</p>
      {t.tags.length ? (
        <ul className="flex flex-wrap gap-1.5">
          {t.tags.map((tag) => (
            <li key={tag} className="rounded-full border border-[var(--mk-border)] px-2 py-0.5 text-[12px] text-[var(--mk-fg-muted)]">
              {tag}
            </li>
          ))}
        </ul>
      ) : null}
      <dl className="mt-auto flex flex-wrap items-baseline gap-x-5 gap-y-1 border-t border-[var(--mk-border)] pt-3 text-[13px] text-[var(--mk-fg-muted)]">
        <div className="flex items-baseline gap-1.5">
          <dd className="text-[17px] font-semibold tabular-nums text-[var(--mk-fg)]">{t.passRate === null ? '—' : `${Math.round(t.passRate * 100)}%`}</dd>
          <dt>{m.fw_tpl_pass()}</dt>
        </div>
        <div className="tabular-nums">{m.fw_tpl_verified({ n: t.verifiedTasks })}</div>
        <div className="tabular-nums">{m.fw_tpl_features({ n: t.features })}</div>
        <div className="tabular-nums">{m.fw_tpl_uses({ n: t.uses })}</div>
      </dl>
    </Link>
  )
}

function Page() {
  const { tag } = Route.useSearch()
  const navigate = useNavigate()
  const { data } = usePublicTemplates(tag)
  const { data: all } = usePublicTemplates(undefined)
  const templates = data?.templates ?? []
  const tags = [...new Set((all?.templates ?? []).flatMap((t) => t.tags))].sort()
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <section className="pb-10 pt-16 lg:pt-24">
          <Container className="flex flex-col items-center gap-6 text-center">
            <Overline>{m.fw_tpl_overline()}</Overline>
            <h1 className="max-w-[20ch]">{m.fw_tpl_title()}</h1>
            <p className="mk-lead max-w-[60ch]">{m.fw_tpl_sub()}</p>
          </Container>
        </section>

        <section className="pb-16">
          <Container className="flex flex-col gap-7">
            {tags.length ? (
              <div className="flex flex-wrap gap-2" role="group" aria-label={m.fw_tpl_filter_all()}>
                <button
                  type="button"
                  onClick={() => navigate({ to: '/templates', search: { tag: undefined } })}
                  className={cn('rounded-full border px-3.5 py-1.5 text-[14px] transition-colors', !tag ? 'border-[var(--mk-fg)] bg-[var(--mk-btn)] text-[var(--mk-btn-fg)]' : 'border-[var(--mk-border)] text-[var(--mk-fg-muted)] hover:text-[var(--mk-fg)]')}
                >
                  {m.fw_tpl_filter_all()}
                </button>
                {tags.map((tg) => (
                  <button
                    key={tg}
                    type="button"
                    onClick={() => navigate({ to: '/templates', search: { tag: tg === tag ? undefined : tg } })}
                    className={cn('rounded-full border px-3.5 py-1.5 text-[14px] transition-colors', tg === tag ? 'border-[var(--mk-fg)] bg-[var(--mk-btn)] text-[var(--mk-btn-fg)]' : 'border-[var(--mk-border)] text-[var(--mk-fg-muted)] hover:text-[var(--mk-fg)]')}
                  >
                    {tg}
                  </button>
                ))}
              </div>
            ) : null}
            {templates.length ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {templates.map((t) => (
                  <Card key={t.id} t={t} />
                ))}
              </div>
            ) : (
              <div className="mk-card flex flex-col gap-2 p-8 text-center">
                <p className="mk-muted">{m.fw_tpl_empty()}</p>
              </div>
            )}
          </Container>
        </section>

        <section className="py-16 lg:py-20">
          <Container>
            <SectionHead
              title={m.fw_tpl_cta_title()}
              lead={m.fw_tpl_cta_desc()}
              actions={
                <>
                  <MkLink to="/plan" search={{ flow: 'new' }}>
                    {m.fw_tpl_cta_btn()}
                  </MkLink>
                  <MkLink to="/benchmark" variant="secondary" arrow>
                    {m.fw_bm_title()}
                  </MkLink>
                  {/* Parity P5: ready-made recipes with example asks + rules */}
                  <MkLink to="/playbooks" variant="secondary" arrow>
                    {m.pb_title()}
                  </MkLink>
                </>
              }
            />
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
